import type { ReactNode } from "react";

export interface WizardFieldFooterProps {
  isInvalid?: boolean;
  /** Validation message rendered when `isInvalid`. */
  errorMessage?: ReactNode;
  /** Success message rendered once the field has validated clean. Suppressed while `isInvalid`. */
  successMessage?: ReactNode;
  /** Fallback for the message slot when neither error nor success shows — pass a fully-formed
   *  element (e.g. WizardTextField's fit hint through TextFieldDescription). */
  hint?: ReactNode;
  /** Count text; omit to render no count. */
  countText?: string;
  /** Hide the count from AT when a hint already conveys the length state to it — avoids the
   *  numeric echo double-announcing on every keystroke. Otherwise the count stays audible
   *  (aria-live) because nothing else describes it. */
  isCountHidden?: boolean;
  /** id for the message `<p>` — from useWizardFieldMessage, wired to the input's aria-describedby. */
  messageId?: string;
  /** id for the count `<p>` — same manual aria-describedby wiring as `messageId`. */
  countId?: string;
  /** Slot classes from the consumer's tv (`messageSlot`/`message`/`count`). */
  classNames: { messageSlot: string; message: string; count?: string };
}

/** Footer row shared by the wizard field family: validation message (left) ↔ character count
 *  (right). Purely presentational — all show/hide policy lives in the consumers. */
export function WizardFieldFooter({
  isInvalid,
  errorMessage,
  successMessage,
  hint,
  countText,
  isCountHidden,
  messageId,
  countId,
  classNames,
}: WizardFieldFooterProps) {
  return (
    <div className={classNames.messageSlot}>
      {isInvalid ? (
        errorMessage ? (
          <p className={classNames.message} id={messageId} role="alert">
            {errorMessage}
          </p>
        ) : null
      ) : successMessage ? (
        <p className={classNames.message} id={messageId}>
          {successMessage}
        </p>
      ) : (
        (hint ?? null)
      )}
      {countText != null ? (
        isCountHidden ? (
          <p aria-hidden="true" className={classNames.count} id={countId}>
            {countText}
          </p>
        ) : (
          <p aria-live="polite" className={classNames.count} id={countId}>
            {countText}
          </p>
        )
      ) : null}
    </div>
  );
}
